import {watchSession} from '../../lib/watchSession';
import SessionRecoveryNotice from '../../components/SessionRecoveryNotice';
import React, { useEffect, useState, useCallback, useLayoutEffect } from "react";
import { useParams, useLocation, Outlet } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import PmShell from "./PmShell";
import { getAuthToken, getProject, setAuthTokenProvider, setCurrentUser } from "./pmApi";
import { canViewPage } from "./accessControl";
import PeopleHandoverPanel from "./PeopleHandoverPanel";

const NAV_KEY_BY_SEGMENT = {
  projects: "home",
  overview: "overview",
  backlog: "backlog",
  board: "board",
  gantt: "gantt",
  "schedule-import": "schedule-import",
  sprints: "sprints",
  budget: "budget",
  report: "budget",
  "audit-log": "audit-log",
  workflow: "workflow",
  workload: "workload",
  automation: "automation",
  permissions: "permissions",
  "project-settings": "project-settings",
  "secretary-room": "secretary-room",
  "demo-dashboard": "demo-dashboard",
  "demo-workflow": "demo-workflow",
  "demo-installation-report": "demo-installation-report",
  "sales-import": "sales-import",
  customers: "sales-import-customers",
  analytics: "sales-import-analytics",
  products: "sales-import-products",
  audit: "sales-import-audit",
  calendar: "calendar",
  people: "people",
  suppliers: "suppliers",
  archive: "archive",
  templates: "templates",
  teams: "teams",
  "resource-workload": "resource-workload",
  "resource-settings": "resource-settings",
  dashboard: "me-dashboard",
  "work-calendar": "me-calendar",
  tasks: "me-tasks",
  settings: "me-settings",
};

const BREADCRUMB_BY_SEGMENT = {
  projects: null, // PmShell already shows "โปรเจกต์ทั้งหมด" as the fallback title
  overview: "ภาพรวม",
  backlog: "Backlog",
  board: "Board",
  gantt: "Gantt/Timeline",
  "schedule-import": "ข้อมูลนำเข้า",
  sprints: "Sprint Planning",
  budget: "งบประมาณ",
  report: "รายงานค่าใช้จ่าย",
  "audit-log": "Audit Log",
  workflow: "จัดการ Workflow",
  workload: "Workload",
  automation: "Automation",
  permissions: "สิทธิ์การเข้าถึง",
  "project-settings": "ตั้งค่าโปรเจกต์",
  "secretary-room": "ห้องทำงานเลขา",
  "demo-dashboard": "Dashboard",
  "demo-workflow": "Demo workflow",
  "demo-installation-report": "รายงานการติดตั้งทดลอง",
  "sales-import": "ข้อมูลการขาย",
  customers: "Customer Detail",
  analytics: "Sales Analytics",
  products: "Product Performance",
  audit: "Audit log ข้อมูลการขาย",
  calendar: "ปฏิทินโครงการ",
  people: "รายชื่อบุคคล",
  suppliers: "Supplier Pool",
  archive: "คลังโปรเจกต์",
  templates: "Templates",
  teams: "จัดการทีม",
  "resource-workload": "Resource Workload",
  "resource-settings": "ตั้งค่าบุคลากร",
  dashboard: "Dashboard ของฉัน",
  "work-calendar": "ปฏิทินงานของฉัน",
  tasks: "งานที่ได้รับมอบหมาย",
  settings: "ตั้งค่าผู้ใช้",
};

const LIVE_REPORT_BREADCRUMB_BY_SEGMENT = {
  "live-report": "ส่วนงานรับแจ้งจาก LineOA",
  "request-calendar": "ปฏิทินงานรับแจ้ง",
  dashboard: "Dashboard ส่วนงานรับแจ้ง",
  statistics: "สถิติและ Activity",
  "person-requests": "Person Requests",
  "person-support": "Person Support",
  "work-overview": "Work Overview",
  "management-type": "Management Type",
  "on-track": "On Track",
  push: "Growth Project",
  strategic: "Strategic Project",
  "all-tasks": "Active Tasks",
  "active-tasks": "Active Tasks",
  "cold-tasks": "Cold Tasks",
  settings: "Settings ส่วนงานรับแจ้ง",
};

const PRE_PROJECT_BREADCRUMB_BY_SEGMENT = {
  dashboard: "Dashboard งานเตรียมเปิดโครงการ",
  list: "รายการงานเตรียมเปิดโครงการ",
  cancelled: "งานยกเลิก",
};

// PmLayout mounts ONCE for every page under it (Outlet pattern) — the
// sidebar/topbar never unmount when navigating between Overview/Backlog/
// Board/Gantt/Sprint or Secretary Room/Calendar/People. Each leaf page used
// to fetch the project and render its own <PmShell>, which meant the whole
// shell remounted (briefly showing the "no project" state) on every click —
// that was the reported flicker. Project data is now fetched once here and
// handed down via useOutletContext().
export default function PmLayout() {
  const { projectId } = useParams();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [fileAccess, setFileAccess] = useState({ email: null, error: false });
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [project, setProject] = useState(null);
  const [viewRank, setViewRank] = useState(3); // default to unrestricted until access resolves, avoids a flash of "no access" for normal users

  useLayoutEffect(() => {
    if (!account) return;
    setCurrentUser(account.username, account.name);
    setAuthTokenProvider(async ({ forceRefresh = false } = {}) => {
      const result = await instance.acquireTokenSilent({ ...loginRequest, account, forceRefresh });
      return result.idToken;
    });
    return watchSession({refresh:getAuthToken,forceRefresh:retryAttempt>0,
      onReady:()=>setFileAccess({email:account.username,error:false}),
      onError:()=>setFileAccess(previous=>({email:previous.email===account.username?previous.email:null,error:true})),
    });
  }, [account?.homeAccountId, account?.username, instance, retryAttempt]);


  const refreshProject = useCallback(() => {
    if (!projectId) { setProject(null); setViewRank(3); return; }
    getProject(projectId).then(p => {
      setProject(p);
      if (p?.access?.viewRank) setViewRank(p.access.viewRank);
    }).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (account && fileAccess.email === account.username) refreshProject();
  }, [refreshProject, fileAccess.email, account]);

  const segments = location.pathname.split("/").filter(Boolean);
  const segment = segments.at(-1);
  const inLiveReport = segments[0] === "live-report";
  const inPreProject = segments[0] === "pre-project-control";
  const inSalesImportAudit = segments[0] === "sales-import" && segment === "audit";
  const inSalesImportCustomers = segments[0] === "sales-import" && segment === "customers";
  const inSalesImportAnalytics = segments[0] === "sales-import" && segment === "analytics";
  const inSalesImportProducts = segments[0] === "sales-import" && segment === "products";
  const navKey = inPreProject
    ? `pre-project-${segment || "dashboard"}`
    : inLiveReport
    ? `live-report-${segment}`
    : inSalesImportAudit
      ? "sales-import-audit"
      : inSalesImportCustomers
        ? "sales-import-customers"
        : inSalesImportAnalytics
          ? "sales-import-analytics"
          : inSalesImportProducts
            ? "sales-import-products"
            : NAV_KEY_BY_SEGMENT[segment];
  const breadcrumb = inPreProject
    ? PRE_PROJECT_BREADCRUMB_BY_SEGMENT[segment] ?? "งานเตรียมเปิดโครงการ"
    : inLiveReport
    ? LIVE_REPORT_BREADCRUMB_BY_SEGMENT[segment] ?? "ส่วนงานรับแจ้งจาก LineOA"
    : BREADCRUMB_BY_SEGMENT[segment];
  const allowed = canViewPage(navKey, viewRank);

  const retrySession=()=>setRetryAttempt(value=>value+1);
  const loginAgain=async()=>{
    try{await instance.loginRedirect({...loginRequest,loginHint:account?.username,redirectStartPage:window.location.href});}
    catch{setFileAccess(previous=>({...previous,error:true}));}
  };
  if(fileAccess.email!==account?.username||!account)return <SessionRecoveryNotice initial failed={fileAccess.error} onRetry={retrySession} onLogin={loginAgain}/>;
  return (
    <PmShell project={project} activeNav={navKey} breadcrumb={breadcrumb} viewRank={viewRank}>
      {fileAccess.error&&<SessionRecoveryNotice failed onRetry={retrySession} onLogin={loginAgain}/>}
      {allowed ? (
        <>
          {(["dashboard", "overview", "demo-dashboard", "analytics"].includes(segment)) && <PeopleHandoverPanel module={inLiveReport ? "secretary" : inPreProject ? "preproject" : segment === "demo-dashboard" ? "demo" : undefined} projectId={projectId} />}
          <Outlet context={{ project, refreshProject, viewRank }} />
        </>
      ) : (
        <div className="pm-state-block">
          <div style={{ fontSize: 36 }}>🔒</div>
          <div style={{ fontWeight: 700 }}>ไม่มีสิทธิ์เข้าถึงหน้านี้</div>
          <div style={{ color: "var(--pm-ink-500)", fontSize: 13 }}>ติดต่อ Owner ของโปรเจกต์นี้หากต้องการสิทธิ์เพิ่ม</div>
        </div>
      )}
    </PmShell>
  );
}


